import React, { useState } from "react";
import cogoToast from "cogo-toast";

/**
 * ShareButton — reusable share button for feed cards, editorial articles,
 * leaderboard cards, and community posts.
 *
 * Per PRD §7.2: "Share button on editorial articles, leaderboard cards,
 * and Community posts that generates a shareable link or
 * WhatsApp-formatted snippet."
 *
 * Uses the Web Share API where available (mobile), falls back to
 * clipboard copy + WhatsApp share link on desktop.
 */
const ShareButton = ({ url, title = "Workbrook", text = "", style = {} }) => {
  const [showMenu, setShowMenu] = useState(false);

  const shareUrl = url || window.location.href;
  const shareText = text ? `${text}\n\n${shareUrl}` : shareUrl;

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator
        .share({ title, text, url: shareUrl })
        .catch(() => {})
        .finally(() => setShowMenu(false));
    } else {
      setShowMenu(!showMenu);
    }
  };

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      cogoToast.success("Link copied to clipboard", {
        position: "top-center",
        hideAfter: 3,
      });
    }
    setShowMenu(false);
  };

  const handleWhatsAppShare = () => {
    const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(waUrl, "_blank");
    setShowMenu(false);
  };

  const handleTwitterShare = () => {
    const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text || title
    )}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twUrl, "_blank");
    setShowMenu(false);
  };

  const handleLinkedInShare = () => {
    const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      shareUrl
    )}`;
    window.open(liUrl, "_blank");
    setShowMenu(false);
  };

  return (
    <div style={{ position: "relative", display: "inline-block", ...style }}>
      <button
        type="button"
        onClick={handleNativeShare}
        style={btnStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#f8f9fa";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
        }}
        title="Share"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
        <span>Share</span>
      </button>

      {showMenu && (
        <>
          {/* Click-away overlay */}
          <div
            onClick={() => setShowMenu(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 998,
            }}
          />
          <div style={menuStyle}>
            <button style={menuItemStyle} onClick={handleWhatsAppShare}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp
            </button>
            <button style={menuItemStyle} onClick={handleTwitterShare}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#1DA1F2">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
              </svg>
              X / Twitter
            </button>
            <button style={menuItemStyle} onClick={handleLinkedInShare}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#0077B5">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              LinkedIn
            </button>
            <button style={menuItemStyle} onClick={handleCopyLink}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#64748b"
                strokeWidth="2"
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Copy link
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const btnStyle = {
  background: "none",
  border: "none",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  cursor: "pointer",
  color: "#64748b",
  fontSize: "14px",
  fontWeight: "500",
  padding: "4px 8px",
  borderRadius: "6px",
  transition: "background-color 0.2s",
};

const menuStyle = {
  position: "absolute",
  bottom: "100%",
  right: 0,
  marginBottom: "6px",
  backgroundColor: "#fff",
  borderRadius: "10px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  border: "1px solid #e8eaf0",
  padding: "6px",
  zIndex: 999,
  minWidth: "160px",
};

const menuItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  width: "100%",
  padding: "8px 12px",
  background: "none",
  border: "none",
  fontSize: "14px",
  color: "#334155",
  cursor: "pointer",
  borderRadius: "6px",
  textAlign: "left",
  transition: "background-color 0.15s",
};

export default ShareButton;
